import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const LoginForm = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Limpiar error del campo cuando el usuario empiece a escribir
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'El email no es válido';
        }

        if (!formData.password) {
            newErrors.password = 'La contraseña es requerida';
        } else if (formData.password.length < 6) {
            newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        
        try {
            await login(formData.email, formData.password, formData.rememberMe);
            toast.success('¡Bienvenido de vuelta!');
            navigate('/');
        } catch (error) {
            console.error('Error en login:', error);
            toast.error(error.message || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    const handleSocialLogin = (provider) => {
        toast.info(`Próximamente: Login con ${provider}`);
    };

    return (
        <div className="login-form-container">
            <Card className="login-card">
                <div className="text-center mb-4">
                    <h2 className="text-3xl font-bold text-900 mb-2">
                        Iniciar Sesión
                    </h2>
                    <p className="text-600">
                        Ingresa a tu cuenta para continuar
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="field mb-4">
                        <label htmlFor="email" className="block text-900 font-medium mb-2">
                            Email
                        </label>
                        <InputText
                            id="email"
                            type="email"
                            placeholder="tu@email.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`w-full ${errors.email ? 'p-invalid' : ''}`}
                        />
                        {errors.email && (
                            <small className="p-error block mt-1">
                                {errors.email}
                            </small>
                        )}
                    </div>

                    <div className="field mb-4">
                        <label htmlFor="password" className="block text-900 font-medium mb-2">
                            Contraseña
                        </label>
                        <Password
                            id="password"
                            placeholder="Tu contraseña"
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            className={`w-full ${errors.password ? 'p-invalid' : ''}`}
                            feedback={false}
                            toggleMask
                        />
                        {errors.password && (
                            <small className="p-error block mt-1">
                                {errors.password}
                            </small>
                        )}
                    </div>

                    <div className="flex align-items-center justify-content-between mb-4">
                        <div className="flex align-items-center">
                            <Checkbox
                                id="rememberMe"
                                checked={formData.rememberMe}
                                onChange={(e) => handleInputChange('rememberMe', e.checked)}
                            />
                            <label htmlFor="rememberMe" className="ml-2 text-900">
                                Recordarme
                            </label>
                        </div>
                        <Link 
                            to="/forgot-password" 
                            className="text-primary hover:text-primary-600 no-underline"
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>
                    </div>

                    <Button
                        type="submit"
                        label="Iniciar Sesión"
                        className="w-full p-button-lg mb-4"
                        loading={loading}
                    />
                </form>

                <Divider align="center">
                    <span className="text-600 px-3">O continúa con</span>
                </Divider>

                <div className="flex gap-2 mb-4">
                    <Button
                        label="Google"
                        icon="pi pi-google"
                        className="flex-1 p-button-outlined"
                        onClick={() => handleSocialLogin('Google')}
                    />
                    <Button
                        label="Facebook"
                        icon="pi pi-facebook"
                        className="flex-1 p-button-outlined"
                        onClick={() => handleSocialLogin('Facebook')}
                    />
                </div>

                <div className="text-center">
                    <span className="text-600">¿No tienes una cuenta? </span>
                    <Link 
                        to="/register" 
                        className="text-primary hover:text-primary-600 no-underline font-medium"
                    >
                        Regístrate aquí
                    </Link>
                </div>
            </Card>
        </div>
    );
};

export default LoginForm;
